import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Bed, Users, Clock } from 'lucide-react';
import { sanitizeRichTextHtml } from '@/lib/richtext/sanitizeRichTextHtml';
import { HeroCarousel, type HeroImage } from '@/components/place/HeroCarousel';
import { PlaceCoreGalleryStrip } from '@/components/placecore/public/PlaceCoreGalleryStrip';

/**
 * AccommodationPagePreview — page-like, non-interactive preview that mirrors
 * the public accommodation page. Used in the editor's main (left) column.
 *
 * Follows the preview-first editorial standard:
 *   Hero → Gallery strip → Title/meta → Price → Description → CTA (disabled)
 *
 * Driven entirely by local form state — no API calls.
 */

interface AccommodationPagePreviewProps {
  title: string;
  descriptionShort?: string | null;
  description?: string | null;
  priceLabel?: string | null;
  beds?: string | null;
  guestCapacity?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  houseRules?: string | null;
  /** Hero images array (cover + gallery) */
  heroImages: HeroImage[];
  /** Thumbnail URLs for gallery strip */
  galleryThumbs: string[];
  /** Current hero carousel index */
  heroIndex: number;
  /** Callback when hero index changes */
  onHeroIndexChange: (index: number) => void;
}

export function AccommodationPagePreview({
  title,
  descriptionShort,
  description,
  priceLabel,
  beds,
  guestCapacity,
  checkInTime,
  checkOutTime,
  houseRules,
  heroImages,
  galleryThumbs,
  heroIndex,
  onHeroIndexChange,
}: AccommodationPagePreviewProps) {
  const hasHero = heroImages.length > 0;
  const cleanDescription = description ? sanitizeRichTextHtml(description) : null;

  const hasMeta = !!(beds || guestCapacity || (checkInTime && checkOutTime));

  return (
    <div className="rounded-2xl border border-border/30 bg-card shadow-sm overflow-hidden">
      {/* Hero — carousel, single image, or collapse entirely */}
      {hasHero && (
        <div className="rounded-t-2xl overflow-hidden">
          {heroImages.length > 1 ? (
            <div className="relative aspect-[4/3] sm:aspect-[16/9] w-full overflow-hidden">
              <HeroCarousel
                images={heroImages}
                className="absolute inset-0 w-full h-full"
                onIndexChange={onHeroIndexChange}
                activeIndex={heroIndex}
              />
            </div>
          ) : (
            <img
              src={heroImages[0].src}
              alt={heroImages[0].alt}
              className="w-full aspect-[4/3] sm:aspect-[16/9] object-cover"
            />
          )}
        </div>
      )}

      {/* Gallery strip */}
      {galleryThumbs.length >= 2 && (
        <div className="px-6 pt-3">
          <PlaceCoreGalleryStrip
            thumbUrls={galleryThumbs}
            fullUrls={heroImages.map(img => img.src)}
            activeHeroIndex={heroIndex}
            onSelectIndex={onHeroIndexChange}
          />
        </div>
      )}

      {/* Content area */}
      <div className={cn('px-8 pb-8 max-w-2xl', hasHero ? 'pt-5' : 'pt-8')}>
        {/* Title */}
        <h1 className="font-display text-2xl font-bold text-foreground leading-tight tracking-tight">
          {title || (
            <span className="text-muted-foreground/40 italic font-normal text-xl">
              Namnlöst boende
            </span>
          )}
        </h1>

        {/* Meta row — beds, guests, check-in/out */}
        {hasMeta && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-muted-foreground">
            {beds && (
              <span className="flex items-center gap-1.5">
                <Bed className="h-3.5 w-3.5 shrink-0" />
                {beds} bäddar
              </span>
            )}
            {guestCapacity && (
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 shrink-0" />
                Max {guestCapacity} gäster
              </span>
            )}
            {checkInTime && checkOutTime && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                {checkInTime} – {checkOutTime}
              </span>
            )}
          </div>
        )}

        {/* Price */}
        {priceLabel ? (
          <p className="text-xl font-semibold text-foreground mt-4">{priceLabel}</p>
        ) : (
          <p className="text-sm text-muted-foreground/60 italic mt-4">Pris visas vid förfrågan</p>
        )}

        {/* Short description */}
        {descriptionShort && (
          <p className="text-base text-muted-foreground leading-relaxed mt-5 line-clamp-4">
            {descriptionShort}
          </p>
        )}

        {/* CTA (disabled, non-interactive) */}
        <div className="mt-6">
          <button
            disabled
            className="inline-flex items-center justify-center rounded-lg bg-primary/80 text-primary-foreground px-6 py-2.5 text-sm font-medium opacity-60 cursor-default"
          >
            Boka boende
          </button>
          <p className="text-[10px] text-muted-foreground/40 mt-1.5">
            Förhandsvisning — knappen är inte aktiv
          </p>
        </div>

        {/* Extended description */}
        {cleanDescription && (
          <div className="mt-6 pt-4 border-t border-border/30">
            <div
              className="prose prose-sm prose-muted max-w-none text-foreground/80 leading-relaxed [&>*:first-child]:mt-0 line-clamp-[8]"
              dangerouslySetInnerHTML={{ __html: cleanDescription }}
            />
          </div>
        )}

        {/* House rules */}
        {houseRules && (
          <div className="mt-5 pt-4 border-t border-border/30 space-y-1.5">
            <h3 className="text-sm font-semibold text-foreground">Husregler</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line line-clamp-4">{houseRules}</p>
          </div>
        )}
      </div>
    </div>
  );
}
